import { t } from '@lingui/core/macro';
import { IPublicSession, updatePublicSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { sendApiRequest } from 'utils/network';
import { RTCClient } from 'webrtc';
import { EStreamSessionEvents } from 'webrtc/events';
import { SignalerClient } from 'webrtc/signal';
import { isDebugMode } from './debug';

export let broadcastingConnection: SignalerClient;
export let voipRtcConnection: RTCClient;
export let broadcastingConnectionId: string;

// Guard flag to prevent infinite broadcast loops
let isApplyingBroadcastUpdate = false;

async function getIceServers() {
  // if ICE_SERVERS_ORIGIN is not set, it will go to the default value which is fine for our deployed instances
  return sendApiRequest<RTCIceServer[]>('/ice-servers', { origin: process.env.ICE_SERVERS_ORIGIN });
}

export function initSessionBroadcasting() {
  // Sometimes session ID is not yet available. If so, exit early and retry when session ID becomes available
  if (!userSession().get('id')) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let unsubscribed = false;

    const unsub = userSession().on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
      if (changedKeys.includes('id') && session.id && !unsubscribed) {
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribed = true;
        unsub();
        initSessionBroadcasting();
      }
    });

    // Failsafe: If session ID is not set within 10 seconds, log error and unsubscribe
    timeoutId = setTimeout(() => {
      if (!unsubscribed) {
        unsubscribed = true;
        unsub();
        console.error('[Session Broadcast] Timeout waiting for session ID - broadcasting not initialized');
      }
    }, 10000);

    logger.log('[Session Broadcast] Waiting for session ID to initialize broadcasting');
    return;
  }

  const broadcastingApiUrl = `${process.env.SIGNALING_ORIGIN}?sessionId=${userSession().get('id')}&topicId=session`;

  if (broadcastingConnection && broadcastingConnection.isReady()) {
    if (broadcastingConnection.wsApiUrl !== broadcastingApiUrl) {
      // URL changed, disconnect and reconnect
      console.warn('[Session Broadcast] Broadcasting connection URL changed, disconnecting');
      broadcastingConnection.disconnect();
    } else {
      // same URL, no need to reconnect
      return;
    }
  }

  broadcastingConnection = new SignalerClient(broadcastingApiUrl);
  broadcastingConnection.connect().then((connection) => {
    broadcastingConnectionId = connection.connectionId;
    logger.info('[Session Broadcast] WebSocket connected', { connectionId: connection.connectionId });
  });
  logger.log('[Session Broadcast] Broadcasting connection initialized');

  userSession().markRequirementReady('broadcastingEstablished');

  broadcastingConnection.on(EStreamSessionEvents.SessionUpdated, (data) => {
    logger.log('[Session Broadcast] Session update received', data);

    if (!data || typeof data !== 'object') {
      return;
    }

    const aveSessionId = (data as { aveSessionId?: string }).aveSessionId;
    logger.info('[Session Broadcast] aveSessionId from broadcast update', aveSessionId);

    // Guard against a stale server echo overwriting an aveSessionId set locally
    // by the integrator (e.g. LCC) in connectionCallback. The echo may carry
    // aveSessionId:'' if share()'s updatePublicSessionNow() fired before the
    // integrator's write reached the server — AVS-4267.
    const sessionData = data as Partial<IPublicSession>;
    let safeData: Partial<IPublicSession> = sessionData;
    if (aveSessionId === '' && userSession().get('aveSessionId')) {
      const copy: Partial<IPublicSession> = { ...sessionData };
      delete copy.aveSessionId;
      safeData = copy;
    }

    // Skip flushing if already processing a broadcast to prevent infinite loops
    if (isApplyingBroadcastUpdate) {
      userSession().setValues(safeData);
      updatePublicSession.cancel();
      return;
    }

    // Discard stale pending local updates — the server broadcast is authoritative.
    // The original 3D→2D reversion bug is prevented by atomic aveSessionId
    // clearing in experience.ts + immediate sync in syncToServer.ts.
    isApplyingBroadcastUpdate = true;
    try {
      updatePublicSession.cancel();
      userSession().setValues(safeData);
      updatePublicSession.cancel();
    } finally {
      isApplyingBroadcastUpdate = false;
    }
  });
}

export async function initWebRtc(
  sessionId = userSession().get('id'),
  callback?: () => void
): Promise<RTCClient | null> {
  // only remote sessions can initialize webrtc and voip should be allowed
  if (!userSession().voipEnabled) {
    // voip not allowed
    return null;
  }

  // already connected, call callback immediately if provided
  if (voipRtcConnection?.sessionId === sessionId) {
    if (callback) {
      callback();
    }
    return voipRtcConnection;
  }

  // if presentation if completed but we try to init rtc, then something is wrong.
  // proceeding is not allowed and also make sure that the previous connection is properly destroyed
  if (userSession().presentationCompleted) {
    voipRtcConnection?.destroy();
    return null;
  }

  // Try to initialize camera stream, but continue establishing RTC connection even if it fails
  // This allows receive-only mode for users who deny camera/mic permissions
  if (!myCameraStream().stream) {
    const initPromise = new Promise<void>((resolve) => {
      let unsubscribeStarted: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;

      const onStreamStarted = () => {
        cleanup();
        resolve();
      };

      const onStreamError = () => {
        cleanup();
        // Don't abort - continue in receive-only mode
        logger.log('[VOIP] Camera/mic denied - continuing in receive-only mode');

        // Show warning toast to inform user about receive-only mode
        showToast({
          title: t`Camera and microphone unavailable`,
          message: t`Your camera and/or microphone are not allowed or not available. You will only be able to see the other party's video stream. To enable your camera and microphone, update your browser permissions and reload the page.`,
          type: 'warn',
          notificationId: 'WARN_VOIP_RECEIVE_ONLY',
        });

        resolve();
      };

      const cleanup = () => {
        if (unsubscribeStarted) unsubscribeStarted();
        if (unsubscribeError) unsubscribeError();
      };

      unsubscribeStarted = myCameraStream().once('stream:started', onStreamStarted);
      unsubscribeError = myCameraStream().once('stream:error', onStreamError);

      myCameraStream().init();
    });

    // Wait for stream initialization attempt, then continue regardless of result
    await initPromise;
  }

  const iceServers = await getIceServers();
  if (!iceServers) {
    console.error(`No ICE servers found, RTC won't work`);
    return null;
  }
  voipRtcConnection = RTCClient.getInstance(
    iceServers,
    `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=user-stream&userType=${user.type}`
  );
  voipRtcConnection.sessionId = sessionId;

  if (callback) {
    voipRtcConnection.on('voip:connected', callback);
  }

  await voipRtcConnection.connect();

  voipRtcConnection.on('peer:disconnected', () => {
    showToast({
      title: t`Your peer disconnected from the VOIP session`,
      type: 'warn',
      notificationId: 'WARN_VOIP_PEER_DISCONNECTED',
    });
  });

  voipRtcConnection.on('peer:connected', () => {
    showToast({
      title: t`Your peer connected to the VOIP session`,
      type: 'success',
      notificationId: 'SUCCESS_VOIP_PEER_CONNECTED',
    });
  });

  userSession().once('session:end', () => {
    voipRtcConnection?.destroy();
    broadcastingConnection?.disconnect();
  });

  if (isDebugMode()) {
    // @ts-expect-error - expose config in global scope for debugging
    window.voipRtcConnection = voipRtcConnection;
  }

  return voipRtcConnection;
}
